import * as React from 'react';

import Button from 'components/Button';
import Image from 'components/Image';
import Divider from 'components/Divider';
import SyncTypePickerConstants from 'constants/SyncTypePickerConstants';
import Columns2 from 'components/Columns2';
import Container from 'components/Container';
import Heading from 'components/Heading';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import Row from 'components/Row';
import Text from 'components/Text';
import { SyncType } from 'globalTypes';

type Props = {
  selectSyncType: (method: SyncType) => void;
  onClose: () => void;
  syncType: SyncType;
  displaySyncType: (syncType: SyncType) => void;
};

type SyncTypeTextProperties = {
  image: string;
  continueWith: string;
  switchTo: string;
  footerText: string;
  footerNextSyncType: SyncType;
  text: string;
  textLink: string;
  subtext: string;
  nextSyncType: SyncType;
};

const textFromSyncType: { [id: string]: SyncTypeTextProperties } = {
  remindSpreadsheet: SyncTypePickerConstants.REMIND,
  remindSpreadsheetV2: SyncTypePickerConstants.REMIND2,
  oneRosterSpreadsheet: SyncTypePickerConstants.ONEROSTER,
};

class AdminSyncTypeSpreadsheetView extends React.Component<Props> {
  renderFirstRowText(syncType: SyncType, textProperties: SyncTypeTextProperties) {
    return (
      <Container>
        <Row>
          <Heading align="left">
            1. Export school data from your SIS in the{' '}
            <Link
              target="_blank"
              href={textProperties.textLink}
              data-test={`choice-hc-article-${syncType}`}>
              {textProperties.text}.
            </Link>
          </Heading>
        </Row>
        <Text align="left" color="thunder" size="small">
          Depending on your SIS, you can alternatively choose the{' '}
          <Link
            onClick={() => {
              this.props.displaySyncType(textProperties.nextSyncType);
            }}
            data-test={`next-sync-type-${syncType}`}>
            {textProperties.subtext} CSV format.
          </Link>
        </Text>
      </Container>
    );
  }

  renderRows(syncType: SyncType, textProperties: SyncTypeTextProperties) {
    return (
      <Container size="xmedium">
        <Columns2>
          {this.renderFirstRowText(syncType, textProperties)}
          <Row size="small">
            <Image src={textProperties.image} />
          </Row>
        </Columns2>
        <Divider size="large" />
        <Columns2>
          <Container>
            <Row>
              <Heading align="left">
                2. Transfer CSV files using{' '}
                <Link target="_blank" href={LinkConstants.SETUP_SFTP_DIRECT_SYNC}>
                  SFTP
                </Link>
                .
              </Heading>
            </Row>
            <Text align="left" color="thunder" size="small">
              Once you've established a connection you can setup a recurring sync between your SIS
              and Remind.
            </Text>
          </Container>
          <Row size="medium">
            <Image width="88%" src={require('assets/images/rostering/sftp.png')} />
          </Row>
        </Columns2>
      </Container>
    );
  }

  handleHideRequest = () => {
    this.props.onClose();
  };

  handleContinue = (syncType: SyncType) => {
    this.props.selectSyncType(syncType);
    this.handleHideRequest();
  };

  render() {
    const { syncType } = this.props;
    const textProperties = textFromSyncType[syncType];

    return (
      <React.Fragment>
        <Row size="medium">
          <Heading align="left"> You need to be able to: </Heading>
        </Row>
        {this.renderRows(syncType, textProperties)}
        <Container>
          <Text align="left">
            <Row size="medium">
              <Button
                data-test-selector="continue-select-sync-type"
                onClick={() => this.handleContinue(syncType)}
                level="primary">
                Continue with {textProperties.continueWith}
              </Button>
              <span> </span>
              <Button
                data-test-selector="switch-sync-type"
                onClick={() => this.props.displaySyncType(textProperties.nextSyncType)}
                level="secondary">
                Switch to {textProperties.switchTo}
              </Button>
            </Row>
          </Text>
          <Text align="left" color="thunder" size="small">
            It’s also possible to{' '}
            <Link
              onClick={() => {
                this.props.displaySyncType(SyncType.clever);
              }}>
              {' '}
              sync with Clever
            </Link>
            , however syncing CSVs directly with Remind is recommended. Syncing CSV files allows
            multiple points of contact information per person and it’s easier to troubleshoot. You
            can also{' '}
            <Link
              onClick={() => {
                this.props.displaySyncType(textProperties.footerNextSyncType);
              }}
              data-test={`next-sync-type-footer-${syncType}`}>
              sync with {textProperties.footerText}.
            </Link>
          </Text>
        </Container>
      </React.Fragment>
    );
  }
}

export default AdminSyncTypeSpreadsheetView;
